import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Badge, type BadgeVariant } from '../components/ui/Badge';
import { Table, THead, TBody, TR, TH, TD } from '../components/ui/Table';
import { PageLoader } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { apiFetch } from '../lib/apiFetch';
import { formatDate } from '../lib/format';
import { useT } from '../i18n';

interface UserItem {
  id: number;
  telegram_id: number;
  username: string | null;
  full_name: string | null;
  phone: string | null;
  region_code: string | null;
  status_code: string;
  admin_role: string | null;
  is_blocked: boolean;
  created_at: string;
}

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  active: 'success',
  registered: 'info',
  new: 'muted',
  expired: 'warning',
  blocked: 'destructive',
};

export function Users() {
  const t = useT();
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState('');

  const list = useQuery({
    queryKey: ['users', search, status],
    queryFn: () =>
      apiFetch<{ items: UserItem[]; total: number }>('/api/admin/users', {
        query: { search, status, limit: 50, offset: 0 },
      }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <form
          className="flex flex-1 gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput.trim());
          }}
        >
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder={t('users.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="outline" size="md">
            {t('common.search')}
          </Button>
        </form>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
        >
          <option value="">{t('users.allStatuses')}</option>
          <option value="new">new</option>
          <option value="registered">registered</option>
          <option value="active">active</option>
          <option value="expired">expired</option>
          <option value="blocked">blocked</option>
        </select>
      </div>

      {list.isLoading ? (
        <PageLoader />
      ) : !list.data || list.data.items.length === 0 ? (
        <EmptyState title={t('users.emptyTitle')} description={t('users.emptyDescription')} />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>{t('users.fullName')}</TH>
                  <TH>Telegram</TH>
                  <TH>{t('users.region')}</TH>
                  <TH>{t('users.registeredAt')}</TH>
                  <TH>{t('common.status')}</TH>
                  <TH>{t('common.role')}</TH>
                </TR>
              </THead>
              <TBody>
                {list.data.items.map((u) => (
                  <TR key={u.id} className="cursor-pointer">
                    <TD>
                      <Link to={`/users/${u.id}`} className="font-medium text-slate-900 hover:underline">
                        {u.full_name ?? '—'}
                      </Link>
                    </TD>
                    <TD>
                      <div className="text-xs">
                        {u.username && <div>@{u.username}</div>}
                        <div className="font-mono text-slate-500">{u.telegram_id}</div>
                      </div>
                    </TD>
                    <TD className="text-sm text-slate-600">{u.region_code ?? '—'}</TD>
                    <TD className="text-sm text-slate-600">{formatDate(u.created_at)}</TD>
                    <TD>
                      <Badge variant={STATUS_VARIANT[u.status_code] ?? 'default'}>{u.status_code}</Badge>
                    </TD>
                    <TD>
                      {u.admin_role ? (
                        <Badge variant="info">{u.admin_role}</Badge>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-slate-500">
        {t('users.totalShown', {
          total: list.data?.total ?? 0,
          shown: list.data?.items.length ?? 0,
        })}
      </p>
    </div>
  );
}
